import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

interface MerchantInfo {
  status: string;
  message: string;
  name: string;
  email: string;
  business_name: string;
  logo: string;
  amount: number;
  title: string;
}

interface AccountInfo {
  success: boolean;
  message: string;
  data: {
    user_id: number;
    amount: number;
    sort_code: string | null;
    bank: string;
    expires_at: string;
    account_name: string;
    account_number: string;
    account_reference: string;
    payment_reference: string;
    provider: string | null;
    updated_at: string;
    created_at: string;
    id: number;
  };
}

interface PayDataContextProps {
  isLoading: boolean;
  merchantData: any;
  accountData: any;
  payReference: string;
  getRef: () => void;
  fetchData: (reference: string) => Promise<void>;
  handleGenerateNewAccount: () => void;
  isTransferAvailable: boolean;
  resetTimer: () => void;
  randomKey: string;
  businessName: string;
  customerInfo: CustomerInfo;
  setCustomerInfo: (customerInfo: CustomerInfo) => void;
}

interface PayDataProviderProps {
  children: ReactNode;
}

interface CustomerInfo {
  fullName: string;
  email: string;
}

const PayDataContext = createContext<PayDataContextProps | undefined>(
  undefined
);

const url = process.env.REACT_APP_API_URL;

const PayDataProvider: React.FC<PayDataProviderProps> = ({ children }) => {
  const [businessName, setBusinessName] = useState<string>("");
  const [merchantData, setMerchantData] = useState<MerchantInfo | null>(null);
  const [accountData, setAccountData] = useState<AccountInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [payReference, setPayReference] = useState<string>("");
  const [isTransferAvailable, setIsTransferAvailable] =
    useState<boolean>(false);
  const [randomKey, setRandomKey] = useState<string>("");
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    fullName: "",
    email: "",
  });

  const location = useLocation();
  const pathname = location.pathname;

  const paymentLinkReference = pathname.split("/").pop();

  const getRef = () => {
    setPayReference(paymentLinkReference);
  };

  const resetTimer = () => {
    localStorage.setItem("startTime", Date.now().toString());
  };

  const generateNewAccount = useCallback(async () => {
    try {
      setIsLoading(true);

      const requestBody = {
        reference: payReference,
        is_payment_link: !payReference.includes("srp") ? true : false,
      };

      const accountResponse = await axios.post(
        `${url}/v2/payment/generate-account-no`,
        requestBody
      );
      setAccountData(accountResponse.data.data);
      setIsTransferAvailable(true);
      resetTimer();

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [merchantData, payReference]);

  const fetchData = useCallback(async () => {
    try {
      const paymentResponse = await axios.post(`${url}/v2/payment/info`, {
        reference: paymentLinkReference,
      });
      const { business_name, payment_link } = paymentResponse.data.data;
      setMerchantData(payment_link);
      setBusinessName(business_name);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleGenerateNewAccount = () => {
    if (!isTransferAvailable) {
      generateNewAccount();
    }
  };

  const generateRandomKey = () => {
    const length = 10;
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * characters.length)
      );
    }
    setRandomKey(result);
  };

  useEffect(() => {
    fetchData();
    generateRandomKey();
  }, []);

  return (
    <PayDataContext.Provider
      value={{
        isLoading,
        merchantData,
        accountData,
        payReference,
        getRef,
        fetchData,
        handleGenerateNewAccount,
        isTransferAvailable,
        resetTimer,
        randomKey,
        businessName,
        customerInfo,
        setCustomerInfo,
      }}
    >
      {children}
    </PayDataContext.Provider>
  );
};

export const usePayDataContext = () => {
  const context = useContext(PayDataContext);
  if (!context) {
    throw new Error("usePayDataContext must be used within a PayDataProvider");
  }
  return context;
};

export default PayDataProvider;
